import React, { useState, useEffect, Fragment, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";
import { useLocation, useNavigate } from "react-router-dom";
import PrintPayment from "./PrintPayment";

// Debounce hook to delay rapid input changes
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const CollectHtPatientFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const initialPhone = location?.state?.phone || "";
  const procedureId = location?.state?.id;

  // State for phone search
  const [phoneSearch, setPhoneSearch] = useState(initialPhone);
  const debouncedPhoneSearch = useDebounce(phoneSearch, 500);

  // Form state
  const [formData, setFormData] = useState({
    ht_patient: null,
    amount: "",
    advance_amount: "",
    date: new Date().toISOString().split("T")[0],
    selected_currency: "",
    selected_advance_currency: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  // Fetch URL depends on debounced phone search
  const fetchUrl = debouncedPhoneSearch
    ? `ht-patients/?search=${debouncedPhoneSearch}`
    : "ht-patients/";

  // Fetch ht-patients, fetch runs on mount and URL change
  const {
    data: htProcedures,
    isLoading: loadingProcedures,
    isError: errorLoadingProcedures,
    refetch,
  } = useFetchObjects(["ht-patients", fetchUrl], fetchUrl, token);

  // Format fetched procedures and include procedure type
  const procedureData = useMemo(() => {
    if (!htProcedures || !Array.isArray(htProcedures)) return [];
    return htProcedures.map((procedure) => ({
      id: procedure.id,
      label: `${procedure.patient_name} - ${new Date(
        procedure.procedure_date
      ).toLocaleDateString()} - ${
        (procedure.patient_details && procedure.patient_details.phone) ||
        procedure.phone ||
        ""
      } - ${procedure.display_procedure_type || ""}`,
      phone:
        (procedure.patient_details && procedure.patient_details.phone) ||
        procedure.phone ||
        "",
      total_cost: procedure.total_cost,
      total_paid: procedure.total_paid,
      balance_due: procedure.balance_due,
      currency: procedure.currency,
      advance: procedure.advance,
      advance_currency: procedure.advance_currency,
      exchange_rate: procedure.exchange_rate,
      discount: procedure.discount,
      procedureType: procedure.display_procedure_type,
    }));
  }, [htProcedures]);

  // Auto-select if ID provided or only one procedure matches phone
  useEffect(() => {
    if (procedureId && procedureData.length > 0) {
      const foundById = procedureData.find((proc) => proc.id === procedureId);
      if (foundById) {
        setSelectedProcedure(foundById);
        setFormData((prev) => ({
          ...prev,
          ht_patient: foundById.id,
          selected_currency: foundById.currency,
          selected_advance_currency:
            foundById.advance_currency || foundById.currency,
        }));
      }
    } else if (initialPhone && procedureData.length === 1) {
      const foundProcedure = procedureData[0];
      setSelectedProcedure(foundProcedure);
      setFormData((prev) => ({
        ...prev,
        ht_patient: foundProcedure.id,
        selected_currency: foundProcedure.currency,
        selected_advance_currency:
          foundProcedure.advance_currency || foundProcedure.currency,
      }));
    }
  }, [initialPhone, procedureId, procedureData]);

  // When debounced phoneSearch changes, refetch and clear selection
  useEffect(() => {
    refetch();
    setSelectedProcedure(null);
  }, [debouncedPhoneSearch, refetch]);

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 1:
        return "؋";
      case 2:
        return "$";
      case 3:
        return "€";
      default:
        return "";
    }
  };

  const computeConvertedValue = (
    value,
    fromCurrency,
    toCurrency,
    exchangeRate
  ) => {
    if (!value || isNaN(value)) return "";
    if (!fromCurrency || !toCurrency) return value;
    const originalCurrency = parseInt(fromCurrency);
    const chosenCurrency = parseInt(toCurrency);
    const rate = parseFloat(exchangeRate || 1);
    const num = parseFloat(value);
    if (originalCurrency === chosenCurrency) return num;
    if (
      (originalCurrency === 2 || originalCurrency === 3) &&
      chosenCurrency === 1
    ) {
      return num * rate;
    } else if (
      originalCurrency === 1 &&
      (chosenCurrency === 2 || chosenCurrency === 3)
    ) {
      return num / rate;
    }
    return num;
  };

  const getFixedConversion = (
    value,
    fromCurrency,
    toCurrency,
    exchangeRate
  ) => {
    const converted = computeConvertedValue(
      value,
      fromCurrency,
      toCurrency,
      exchangeRate
    );
    return typeof converted === "number" ? Number(converted.toFixed(2)) : "";
  };

  const handleProcedureSelect = (procedure) => {
    setSelectedProcedure(procedure);
    setFormData((prev) => ({
      ...prev,
      ht_patient: procedure?.id || null,
      selected_currency: procedure?.currency || "",
      selected_advance_currency:
        procedure?.advance_currency || procedure?.currency || "",
    }));
    setFormErrors((prev) => ({ ...prev, ht_patient: "" }));
  };

  // Helper function to validate and format amount input
  const validateAndFormatAmount = (value) => {
    if (value === "") return value;

    // Remove any non-digit characters except decimal point
    const cleanedValue = value.replace(/[^0-9.]/g, "");

    // Split into integer and decimal parts
    const parts = cleanedValue.split(".");
    let integerPart = parts[0] || "";
    let decimalPart = parts[1] || "";

    // Ensure only one decimal point
    if (parts.length > 2) {
      decimalPart = parts.slice(1).join("");
    }

    // Limit integer part to 8 digits (to allow 2 decimal digits for total 10)
    if (integerPart.length > 8) {
      integerPart = integerPart.substring(0, 8);
    }

    // Limit decimal part to 2 digits
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }

    // Combine parts
    let formattedValue = integerPart;
    if (decimalPart) {
      formattedValue += `.${decimalPart}`;
    }

    return formattedValue;
  };

  const handleAmountChange = (e) => {
    const formattedValue = validateAndFormatAmount(e.target.value);
    setFormData({ ...formData, amount: formattedValue });
  };

  const handleAdvanceAmountChange = (e) => {
    const formattedValue = validateAndFormatAmount(e.target.value);
    setFormData({ ...formData, advance_amount: formattedValue });
  };

  const {
    handleAdd,
    resetForm,
    loading: saving,
    responseData,
  } = useAdd(
    "ht-payments",
    token,
    t("reception.paymentSuccess"),
    t("reception.paymentFailed")
  );

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.ht_patient)
      errors.ht_patient = t("reception.procedureRequired");
    if (!formData.date) errors.date = t("reception.dateRequired");

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    let dataToSave = {
      ht_patient: formData.ht_patient,
      date: formData.date,
      amount: formData.amount,
      advance_amount: formData.advance_amount,
    };

    // Convert main amount if currency is different
    if (selectedProcedure && formData.selected_currency) {
      dataToSave.amount = computeConvertedValue(
        formData.amount,
        formData.selected_currency,
        selectedProcedure.currency,
        selectedProcedure.exchange_rate
      );
      dataToSave.currency = selectedProcedure.currency;
    }

    // Convert advance amount if advance currency is different
    if (selectedProcedure && formData.selected_advance_currency) {
      const targetAdvanceCurrency =
        selectedProcedure.advance_currency || selectedProcedure.currency;
      dataToSave.advance_amount = computeConvertedValue(
        formData.advance_amount,
        formData.selected_advance_currency,
        targetAdvanceCurrency,
        selectedProcedure.exchange_rate
      );
      dataToSave.advance_currency = targetAdvanceCurrency;
    }

    // Ensure amounts are properly formatted before sending
    const formatForApi = (value) => {
      if (value === undefined || value === "") return "";
      const num = parseFloat(value);
      return num.toFixed(2);
    };

    if (dataToSave.amount !== undefined && dataToSave.amount !== "") {
      dataToSave.amount = formatForApi(dataToSave.amount);
    }
    if (
      dataToSave.advance_amount !== undefined &&
      dataToSave.advance_amount !== ""
    ) {
      dataToSave.advance_amount = formatForApi(dataToSave.advance_amount);
    }

    handleAdd(dataToSave);
  };

  useEffect(() => {
    if (responseData) {
      setPrintData(responseData);
      setTimeout(() => {
        if (printRef.current) {
          const printWindow = window.open("", "_blank");
          printWindow.document.write(printRef.current.innerHTML);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }
      }, 500);
    }
  }, [responseData]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        ht_patient: null,
        amount: "",
        advance_amount: "",
        date: new Date().toISOString().split("T")[0],
        selected_currency: "",
        selected_advance_currency: "",
      });
      setFormErrors({});
      setSelectedProcedure(null);
      setPhoneSearch("");
    }
  }, [resetForm]);

  return (
    <Fragment>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          py: 4,
          minHeight: "60vh",
          width: "100%",
          p: 2,
        }}
      >
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("reception.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("reception.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{ fontSize: isSmallScreen ? "1.8rem" : "2.5rem" }}
          >
            {t("reception.collectHtPatientFees")}
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ fontSize: isSmallScreen ? "0.9rem" : "1rem" }}
          >
            {t("reception.collectHtPatientFeesSubtitle")}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {/* Payment Form Section */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                color="primary"
                mb={2}
                sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
              >
                {t("reception.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  {/* Phone Search Field */}
                  <Grid item xs={12}>
                    <TextField
                      label={t("reception.searchByPhone")}
                      variant="outlined"
                      fullWidth
                      value={phoneSearch}
                      onChange={(e) => setPhoneSearch(e.target.value)}
                      helperText={t("reception.enterPatientPhone")}
                    />
                    {loadingProcedures && (
                      <Box mt={1}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    {errorLoadingProcedures && (
                      <Typography variant="caption" color="error">
                        {t("reception.errorLoadingProcedures")}
                      </Typography>
                    )}
                  </Grid>
                  {/* Procedures List (only if not yet selected) */}
                  {phoneSearch &&
                    !loadingProcedures &&
                    procedureData.length > 0 &&
                    !selectedProcedure && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                          {t("reception.selectProcedure")}
                        </Typography>
                        <List>
                          {procedureData.map((proc) => (
                            <ListItem key={proc.id} disablePadding>
                              <ListItemButton
                                selected={selectedProcedure?.id === proc.id}
                                onClick={() => handleProcedureSelect(proc)}
                              >
                                <ListItemText primary={proc.label} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  {/* Payment fields */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.amount")}
                      variant="outlined"
                      fullWidth
                      type="text" // Changed from number to text for better control
                      value={formData.amount}
                      onChange={handleAmountChange}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9]*.?[0-9]{0,2}",
                      }}
                    />
                    {formData.amount &&
                      selectedProcedure &&
                      formData.selected_currency && (
                        <Typography variant="caption" color="textSecondary">
                          {t("reception.afterExchange")}:{" "}
                          {getFixedConversion(
                            formData.amount,
                            formData.selected_currency,
                            selectedProcedure.currency,
                            selectedProcedure.exchange_rate
                          )}{" "}
                          {getCurrencySymbol(selectedProcedure.currency)}
                        </Typography>
                      )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.date")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.advanceAmount")}
                      variant="outlined"
                      fullWidth
                      type="text" // Changed from number to text for better control
                      value={formData.advance_amount}
                      onChange={handleAdvanceAmountChange}
                      error={!!formErrors.advance_amount}
                      helperText={formErrors.advance_amount}
                      disabled={
                        selectedProcedure &&
                        selectedProcedure.advance &&
                        parseFloat(formData.advance_amount) ===
                          parseFloat(selectedProcedure.advance)
                      }
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9]*.?[0-9]{0,2}",
                      }}
                    />
                    {formData.advance_amount &&
                      selectedProcedure &&
                      formData.selected_advance_currency && (
                        <Typography variant="caption" color="textSecondary">
                          {t("reception.afterExchange")}:{" "}
                          {getFixedConversion(
                            formData.advance_amount,
                            formData.selected_advance_currency,
                            selectedProcedure.advance_currency ||
                              selectedProcedure.currency,
                            selectedProcedure.exchange_rate
                          )}{" "}
                          {getCurrencySymbol(
                            selectedProcedure.advance_currency ||
                              selectedProcedure.currency
                          )}
                        </Typography>
                      )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      component="fieldset"
                      error={!!formErrors.selected_currency}
                    >
                      <FormLabel component="legend">
                        {t("reception.selectCurrency")}
                      </FormLabel>
                      <RadioGroup
                        row
                        name="selected_currency"
                        value={formData.selected_currency}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selected_currency: parseInt(e.target.value),
                          })
                        }
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={getCurrencySymbol(1)}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={getCurrencySymbol(2)}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={getCurrencySymbol(3)}
                        />
                      </RadioGroup>
                      {formErrors.selected_currency && (
                        <Typography variant="caption" color="error">
                          {formErrors.selected_currency}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      component="fieldset"
                      error={!!formErrors.selected_advance_currency}
                    >
                      <FormLabel component="legend">
                        {t(
                          "reception.selectAdvanceCurrency",
                          "Select Advance Currency"
                        )}
                      </FormLabel>
                      <RadioGroup
                        row
                        name="selected_advance_currency"
                        value={formData.selected_advance_currency}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selected_advance_currency: parseInt(e.target.value),
                          })
                        }
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={getCurrencySymbol(1)}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={getCurrencySymbol(2)}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={getCurrencySymbol(3)}
                        />
                      </RadioGroup>
                      {formErrors.selected_advance_currency && (
                        <Typography variant="caption" color="error">
                          {formErrors.selected_advance_currency}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={saving || selectedProcedure?.balance_due === 0}
                    sx={{
                      fontSize: isSmallScreen ? "0.9rem" : "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("reception.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>
          {/* Procedure Details Section */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                  sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                >
                  {t("reception.procedureDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedProcedure ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.patient")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedProcedure.label}
                      </Typography>
                    </Box>
                    {selectedProcedure.phone && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("reception.phoneNumber", "Phone Number")}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {selectedProcedure.phone}
                        </Typography>
                      </Box>
                    )}
                    <Grid container justifyContent={"space-between"}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("reception.totalCost")}
                        </Typography>
                        <Typography variant="h6">
                          {selectedProcedure.total_cost}{" "}
                          {getCurrencySymbol(selectedProcedure.currency)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("call_center.discount")}
                        </Typography>
                        <Typography variant="h6">
                          {selectedProcedure.discount} %
                        </Typography>
                      </Box>
                      {selectedProcedure.advance !== null && (
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {t("reception.advanceAmount")}
                          </Typography>
                          <Typography variant="h6">
                            {selectedProcedure.advance}{" "}
                            {getCurrencySymbol(
                              selectedProcedure.advance_currency ||
                                selectedProcedure.currency
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.exchangeRate", "Exchange Rate")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedProcedure.exchange_rate}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedProcedure.total_paid}{" "}
                        {getCurrencySymbol(selectedProcedure.currency)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.balanceDue")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedProcedure.balance_due}{" "}
                        {getCurrencySymbol(selectedProcedure.currency)}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("reception.noProcedureSelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Hidden Print Component */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <PrintPayment data={printData} />
          </div>
        )}
      </Box>
    </Fragment>
  );
};

export default CollectHtPatientFees;